<template>
  <Teleport to="body">
    <div v-if="isOpen" class="chat-bulk-modal" @click.self="close" @keydown.esc="handleEscape">
      <section class="chat-bulk-modal__panel" role="dialog" aria-modal="true" :aria-label="labels.message_bulk_title">
        <header class="chat-bulk-modal__header">
          <span aria-hidden="true"></span>
          <h2>{{ confirmationOpen ? formTitle : labels.message_bulk_title }}</h2>
          <button ref="closeButton" type="button" :aria-label="labels.close" :disabled="saving" @click="close"><i class="fas fa-times" aria-hidden="true"></i></button>
        </header>

        <div v-if="loading" class="chat-bulk-modal__loading"><span class="ec-loading__spinnerVisual" aria-hidden="true"></span></div>

        <div v-else-if="confirmationOpen" class="chat-bulk-confirm">
          <dl>
            <dt>{{ labels.message_bulk_send_datetime }}</dt>
            <dd>{{ selectedDateLabel }}</dd>
            <dt>{{ labels.message_bulk_body }}</dt>
            <dd>{{ body }}</dd>
          </dl>
          <div class="chat-bulk-actions">
            <button type="button" :disabled="saving" @click="closeSaveConfirmation">{{ labels.back }}</button>
            <button ref="confirmButton" class="chat-bulk-actions__primary" type="button" :disabled="saving" @click="save">{{ editingId === null ? labels.message_bulk_register : labels.message_bulk_change }}</button>
          </div>
        </div>

        <div v-else class="chat-bulk-modal__body">
          <section class="chat-bulk-accordion">
            <button class="chat-bulk-accordion__trigger" type="button" :aria-expanded="newOpen" @click="newOpen = !newOpen">
              <span>{{ formTitle }}</span><i class="fas fa-chevron-down" aria-hidden="true"></i>
            </button>
            <form v-if="newOpen" class="chat-bulk-form" @submit.prevent="confirmSave">
              <label>
                <span>{{ labels.message_bulk_body }}</span>
                <textarea ref="bodyInput" v-model="body" rows="6" :disabled="saving"></textarea>
              </label>
              <fieldset>
                <legend>{{ labels.message_bulk_send_datetime }}</legend>
                <div class="chat-bulk-form__datetime">
                  <select v-model="monthValue" :aria-label="labels.message_bulk_month" :disabled="saving" @change="resetDependentDate">
                    <option value="">{{ labels.message_bulk_month }}</option>
                    <option v-for="option in monthOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
                  </select>
                  <select v-model="day" :aria-label="labels.message_bulk_day" :disabled="saving || monthValue === ''" @change="resetDependentTime">
                    <option value="">{{ labels.message_bulk_day }}</option>
                    <option v-for="value in dayOptions" :key="value" :value="String(value)">{{ value }}日</option>
                  </select>
                  <select v-model="hour" :aria-label="labels.message_bulk_hour" :disabled="saving || day === ''" @change="minute = ''">
                    <option value="">{{ labels.message_bulk_hour }}</option>
                    <option v-for="value in hourOptions" :key="value" :value="String(value)">{{ value }}時</option>
                  </select>
                  <select v-model="minute" :aria-label="labels.message_bulk_minute" :disabled="saving || hour === ''">
                    <option value="">{{ labels.message_bulk_minute }}</option>
                    <option v-for="value in minuteOptions" :key="value" :value="String(value)">{{ pad(value) }}分</option>
                  </select>
                </div>
              </fieldset>
              <div class="chat-bulk-actions">
                <button v-if="editingId !== null" type="button" :disabled="saving" @click="resetForm">{{ labels.cancel }}</button>
                <button class="chat-bulk-actions__primary" type="submit" :disabled="!canConfirm || saving">{{ editingId === null ? labels.message_bulk_register : labels.message_bulk_change }}</button>
              </div>
            </form>
          </section>

          <section class="chat-bulk-accordion">
            <button class="chat-bulk-accordion__trigger" type="button" :aria-expanded="historyOpen" @click="historyOpen = !historyOpen">
              <span>{{ labels.message_bulk_history }}</span><i class="fas fa-chevron-down" aria-hidden="true"></i>
            </button>
            <div v-if="historyOpen" class="chat-bulk-history">
              <p v-if="messages.length === 0" class="chat-bulk-history__empty">{{ labels.message_bulk_empty }}</p>
              <article v-for="message in messages" v-else :key="message.id" class="chat-bulk-message">
                <button class="chat-bulk-message__summary" type="button" :disabled="!isEditable(message)" :aria-expanded="expandedId === message.id" @click="toggleMessage(message)">
                  <span class="chat-bulk-message__meta"><time>{{ dateLabel(message.send_datetime) }}</time><strong :class="isEditable(message) ? 'is-reserved' : 'is-sent'">{{ isEditable(message) ? labels.message_bulk_reserved : labels.message_bulk_sent }}</strong></span>
                  <span class="chat-bulk-message__body">{{ message.body }}</span>
                </button>
                <div v-if="expandedId === message.id && isEditable(message)" class="chat-bulk-message__actions">
                  <button type="button" @click="startCancel(message)">{{ labels.message_bulk_cancel }}</button>
                  <button type="button" @click="startEdit(message)">{{ labels.message_bulk_edit }}</button>
                </div>
              </article>
            </div>
          </section>
        </div>
      </section>

      <div v-if="cancelId !== null" class="chat-bulk-delete-confirm" role="dialog" aria-modal="true" @click.self="closeCancelConfirmation">
        <div>
          <p>{{ labels.message_bulk_cancel_confirm }}</p>
          <button type="button" :disabled="saving" @click="closeCancelConfirmation">{{ labels.close }}</button>
          <button ref="deleteButton" type="button" :disabled="saving" @click="deleteMessage">{{ labels.message_bulk_cancel_execute }}</button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import {computed, nextTick, ref, watch} from 'vue';
import {chatGraphemeLength} from '../services/chatToken.js';

const emit = defineEmits(['notify']);
const props = defineProps({
  initialData: {type: Object, required: true},
  labels: {type: Object, required: true},
  listLoader: {type: Function, default: null},
  saveLoader: {type: Function, default: null},
  deleteLoader: {type: Function, default: null},
});

const isOpen = ref(false);
const loading = ref(false);
const saving = ref(false);
const messages = ref([]);
const newOpen = ref(true);
const historyOpen = ref(false);
const confirmationOpen = ref(false);
const expandedId = ref(null);
const editingId = ref(null);
const cancelId = ref(null);
const body = ref('');
const monthValue = ref('');
const day = ref('');
const hour = ref('');
const minute = ref('');
const closeButton = ref(null);
const confirmButton = ref(null);
const deleteButton = ref(null);
const bodyInput = ref(null);
const opener = ref(null);
const nowMs = ref(Date.now());

const formTitle = computed(() => editingId.value === null ? props.labels.message_bulk_new : props.labels.message_bulk_change_title);
const selectedDate = computed(() => {
  if (monthValue.value === '' || day.value === '' || hour.value === '' || minute.value === '') return null;
  const [year, month] = monthValue.value.split('-').map(Number);
  return new Date(`${year}-${pad(month)}-${pad(Number(day.value))}T${pad(Number(hour.value))}:${pad(Number(minute.value))}:00+09:00`);
});
const selectedDateLabel = computed(() => selectedDate.value === null ? '' : dateLabel(selectedDate.value.toISOString()));
const normalizedBody = computed(() => body.value
  .replace(/\r\n?/g, '\n')
  .normalize('NFC')
  .replace(/^[\p{White_Space}]+|[\p{White_Space}]+$/gu, ''));
const canConfirm = computed(() => {
  const length = chatGraphemeLength(normalizedBody.value);
  return length >= 1 && length <= props.initialData.message_bulk_max_length && selectedDate.value !== null;
});
const currentDate = computed(() => new Date(nowMs.value));
const monthOptions = computed(() => Array.from({length: 2}, (_, index) => {
  const baseYear = Number(jstPart(currentDate.value, 'year'));
  const baseMonth = Number(jstPart(currentDate.value, 'month'));
  const date = new Date(Date.UTC(baseYear, baseMonth - 1 + index, 1));
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth() + 1;
  return {value: `${year}-${pad(month)}`, label: `${month}月`};
}));
const dayOptions = computed(() => {
  if (monthValue.value === '') return [];
  const [year, month] = monthValue.value.split('-').map(Number);
  const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return Array.from({length: last}, (_, index) => index + 1).filter((value) => (
    String(value) === day.value || new Date(`${year}-${pad(month)}-${pad(value)}T23:55:00+09:00`).getTime() > nowMs.value
  ));
});
const hourOptions = computed(() => {
  if (monthValue.value === '' || day.value === '') return [];
  const [year, month] = monthValue.value.split('-').map(Number);
  return Array.from({length: 24}, (_, index) => index).filter((value) => (
    String(value) === hour.value || new Date(`${year}-${pad(month)}-${pad(Number(day.value))}T${pad(value)}:55:00+09:00`).getTime() > nowMs.value
  ));
});
const minuteOptions = computed(() => {
  if (monthValue.value === '' || day.value === '' || hour.value === '') return [];
  const [year, month] = monthValue.value.split('-').map(Number);
  return Array.from({length: 12}, (_, index) => index * 5).filter((value) => (
    String(value) === minute.value || new Date(`${year}-${pad(month)}-${pad(Number(day.value))}T${pad(Number(hour.value))}:${pad(value)}:00+09:00`).getTime() > nowMs.value
  ));
});

function pad(value) { return String(value).padStart(2, '0'); }
function jstPart(date, type) {
  return new Intl.DateTimeFormat('en-US', {timeZone: 'Asia/Tokyo', [type]: 'numeric'}).format(date);
}
const url = (template, id) => template.replace('%bulk_message_id%', encodeURIComponent(String(id)));
const validResult = (response) => response?.status === 'OK' && response.result !== null && typeof response.result === 'object';
const request = (requestUrl, method, data = undefined) => $.ajax({
  url: requestUrl,
  method,
  dataType: 'json',
  ...(data === undefined ? {} : {contentType: 'application/json; charset=UTF-8', data: JSON.stringify(data)}),
});
const updateNow = () => { nowMs.value = Date.now(); };

const open = async (trigger = null) => {
  opener.value = trigger;
  isOpen.value = true;
  newOpen.value = true;
  historyOpen.value = false;
  resetForm();
  updateNow();
  await load();
  await nextTick();
  closeButton.value?.focus();
};
const close = async () => {
  if (saving.value) return;
  isOpen.value = false;
  cancelId.value = null;
  confirmationOpen.value = false;
  await nextTick();
  opener.value?.focus?.();
};
const handleEscape = () => {
  if (cancelId.value !== null) closeCancelConfirmation();
  else if (confirmationOpen.value) closeSaveConfirmation();
  else close();
};
const load = async () => {
  loading.value = true;
  try {
    const response = props.listLoader === null
      ? await request(props.initialData.chat_message_bulk_list_api_url, 'GET')
      : await props.listLoader();
    if (!validResult(response) || !Array.isArray(response.result.messages)) throw new Error('Invalid bulk message list response.');
    messages.value = response.result.messages;
  } catch (error) {
    emit('notify', {type: 'error', message: error?.responseJSON?.message ?? props.labels.message_bulk_load_failed});
  } finally {
    loading.value = false;
  }
};
const resetForm = () => {
  editingId.value = null;
  confirmationOpen.value = false;
  body.value = '';
  monthValue.value = '';
  day.value = '';
  hour.value = '';
  minute.value = '';
};
const resetDependentDate = () => { day.value = ''; hour.value = ''; minute.value = ''; };
const resetDependentTime = () => { hour.value = ''; minute.value = ''; };
const confirmSave = async () => {
  if (!canConfirm.value) return;
  confirmationOpen.value = true;
  await nextTick();
  confirmButton.value?.focus();
};
const closeSaveConfirmation = async () => {
  confirmationOpen.value = false;
  await nextTick();
  closeButton.value?.focus();
};
const payload = () => {
  const [yearValue, monthValueNumber] = monthValue.value.split('-').map(Number);
  return {
    body: body.value,
    send_year: yearValue,
    send_month: monthValueNumber,
    send_day: Number(day.value),
    send_hour: Number(hour.value),
    send_minute: Number(minute.value),
    _token: editingId.value === null ? props.initialData.message_bulk_create_csrf_token : props.initialData.message_bulk_update_csrf_token,
  };
};
const save = async () => {
  if (saving.value || !canConfirm.value) return;
  saving.value = true;
  const id = editingId.value;
  try {
    const response = props.saveLoader === null
      ? await request(id === null ? props.initialData.chat_message_bulk_create_api_url : url(props.initialData.chat_message_bulk_update_api_url_template, id), id === null ? 'POST' : 'PUT', payload())
      : await props.saveLoader({id, payload: payload()});
    if (!validResult(response) || typeof response.result.message !== 'object') throw new Error('Invalid bulk message save response.');
    messages.value = [response.result.message, ...messages.value.filter((message) => message.id !== id)]
      .sort((left, right) => right.send_datetime.localeCompare(left.send_datetime)
        || String(right.id).localeCompare(String(left.id), undefined, {numeric: true}));
    resetForm();
    emit('notify', {type: 'success', message: id === null ? props.labels.message_bulk_created : props.labels.message_bulk_changed});
  } catch (error) {
    if (error?.status === 409) {
      await load();
      resetForm();
    }
    emit('notify', {type: 'error', message: error?.responseJSON?.message ?? props.labels.message_bulk_invalid});
  } finally {
    saving.value = false;
  }
};
const isEditable = (message) => !message.send_flg && new Date(message.send_datetime).getTime() > nowMs.value;
const refreshEditable = (message) => {
  updateNow();
  return isEditable(message);
};
const toggleMessage = (message) => {
  if (refreshEditable(message)) expandedId.value = expandedId.value === message.id ? null : message.id;
  else expandedId.value = null;
};
const startEdit = async (message) => {
  if (!refreshEditable(message)) {
    expandedId.value = null;
    return;
  }
  editingId.value = message.id;
  body.value = message.body;
  const value = message.send_datetime;
  monthValue.value = value.slice(0, 7);
  day.value = String(Number(value.slice(8, 10)));
  hour.value = String(Number(value.slice(11, 13)));
  minute.value = String(Number(value.slice(14, 16)));
  newOpen.value = true;
  expandedId.value = null;
  await nextTick();
  bodyInput.value?.focus();
};
const startCancel = (message) => {
  if (!refreshEditable(message)) {
    expandedId.value = null;
    return;
  }
  cancelId.value = message.id;
};
const closeCancelConfirmation = async () => {
  cancelId.value = null;
  await nextTick();
  closeButton.value?.focus();
};
const deleteMessage = async () => {
  if (saving.value || cancelId.value === null) return;
  saving.value = true;
  const id = cancelId.value;
  try {
    const deletePayload = {_token: props.initialData.message_bulk_delete_csrf_token};
    const response = props.deleteLoader === null
      ? await request(url(props.initialData.chat_message_bulk_delete_api_url_template, id), 'DELETE', deletePayload)
      : await props.deleteLoader({id, payload: deletePayload});
    if (response?.status !== 'OK') throw new Error('Invalid bulk message delete response.');
    messages.value = messages.value.filter((message) => message.id !== id);
    cancelId.value = null;
    expandedId.value = null;
    emit('notify', {type: 'success', message: props.labels.message_bulk_cancelled});
  } catch (error) {
    if (error?.status === 409) {
      await load();
      cancelId.value = null;
    }
    emit('notify', {type: 'error', message: error?.responseJSON?.message ?? props.labels.message_bulk_invalid});
  } finally {
    saving.value = false;
  }
};
const dateLabel = (value) => {
  const parts = Object.fromEntries(new Intl.DateTimeFormat('ja-JP', {
    timeZone: 'Asia/Tokyo',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(value)).map((part) => [part.type, part.value]));
  return `${parts.month}月${parts.day}日 ${parts.hour}時${parts.minute}分`;
};

watch(cancelId, async (id) => { if (id !== null) { await nextTick(); deleteButton.value?.focus(); } });
defineExpose({open});
</script>

<style scoped>
.chat-bulk-modal{position:fixed;z-index:2060;inset:0;display:grid;padding:20px;background:rgba(0,0,0,.4);place-items:center}.chat-bulk-modal__panel{position:relative;width:min(620px,100%);max-height:90vh;border-radius:14px;background:#fff;overflow:auto}.chat-bulk-modal__header{position:sticky;z-index:2;top:0;display:grid;align-items:center;min-height:58px;padding:0 8px;grid-template-columns:44px minmax(0,1fr) 44px;border-bottom:1px solid #eee;background:#fff}.chat-bulk-modal__header h2{margin:0;font-size:18px;text-align:center}.chat-bulk-modal__header button{display:grid;width:44px;height:44px;border:0;background:transparent;place-items:center}.chat-bulk-modal__body{display:grid;gap:14px;padding:16px}.chat-bulk-modal__loading{display:grid;min-height:280px;place-items:center}.chat-bulk-accordion{border:1px solid #e3e3e6;border-radius:10px;overflow:hidden}.chat-bulk-accordion__trigger{display:flex;align-items:center;justify-content:space-between;width:100%;min-height:52px;padding:12px 16px;border:0;background:#f8f8fa;font-weight:700;text-align:left}.chat-bulk-accordion__trigger i{transition:transform .15s}.chat-bulk-accordion__trigger[aria-expanded=true] i{transform:rotate(180deg)}.chat-bulk-form{display:grid;gap:16px;padding:16px}.chat-bulk-form label{display:grid;gap:7px}.chat-bulk-form textarea{width:100%;padding:10px;border:1px solid #ccc;border-radius:7px;resize:vertical}.chat-bulk-form fieldset{margin:0}.chat-bulk-form legend{margin-bottom:7px;font-size:14px}.chat-bulk-form__datetime{display:grid;grid-template-columns:repeat(4,1fr);gap:8px}.chat-bulk-form select{min-width:0;height:42px;padding:0 7px;border:1px solid #ccc;border-radius:6px;background:#fff}.chat-bulk-actions{display:flex;justify-content:flex-end;gap:10px}.chat-bulk-actions button{min-width:100px;min-height:42px;padding:8px 16px;border:1px solid #ddd;border-radius:21px;background:#fff}.chat-bulk-actions .chat-bulk-actions__primary{border-color:#f28b22;color:#fff;background:#f28b22}.chat-bulk-actions button:disabled{opacity:.5}.chat-bulk-history__empty{margin:0;padding:28px 16px;color:#777;text-align:center}.chat-bulk-message{border-top:1px solid #eee}.chat-bulk-message:first-child{border-top:0}.chat-bulk-message__summary{display:grid;gap:7px;width:100%;padding:14px 16px;border:0;background:#fff;text-align:left}.chat-bulk-message__summary:disabled{color:inherit}.chat-bulk-message__meta{display:flex;align-items:center;justify-content:space-between;gap:8px;font-size:13px}.chat-bulk-message__meta strong{padding:3px 8px;border-radius:12px;font-size:12px}.chat-bulk-message__meta .is-reserved{color:#1f7a49;background:#e8f7ee}.chat-bulk-message__meta .is-sent{color:#666;background:#eee}.chat-bulk-message__body{display:-webkit-box;overflow:hidden;line-height:1.55;overflow-wrap:anywhere;-webkit-box-orient:vertical;-webkit-line-clamp:2}.chat-bulk-message__actions{display:grid;padding:0 16px 14px;grid-template-columns:1fr 1fr;gap:10px}.chat-bulk-message__actions button{min-height:40px;border:1px solid #ddd;border-radius:20px;background:#fff}.chat-bulk-message__actions button:last-child{border-color:#f28b22;color:#fff;background:#f28b22}.chat-bulk-confirm{display:grid;gap:22px;padding:22px}.chat-bulk-confirm dl{display:grid;margin:0;grid-template-columns:110px minmax(0,1fr);gap:10px 14px}.chat-bulk-confirm dt{font-weight:700}.chat-bulk-confirm dd{margin:0;white-space:pre-wrap;overflow-wrap:anywhere}.chat-bulk-delete-confirm{position:fixed;z-index:2070;inset:0;display:grid;padding:20px;background:rgba(0,0,0,.4);place-items:center}.chat-bulk-delete-confirm>div{display:grid;width:min(380px,100%);grid-template-columns:1fr 1fr;border-radius:12px;background:#fff;overflow:hidden}.chat-bulk-delete-confirm p{grid-column:1/-1;margin:0;padding:28px 18px;text-align:center}.chat-bulk-delete-confirm button{min-height:48px;border:0;border-top:1px solid #eee;background:#fff}.chat-bulk-delete-confirm button:last-child{color:#fff;background:#d8493f}.chat-bulk-modal button:focus-visible,.chat-bulk-modal textarea:focus-visible,.chat-bulk-modal select:focus-visible{outline:2px solid #2dbb69;outline-offset:2px}@media(max-width:767px){.chat-bulk-modal{padding:0;align-items:end}.chat-bulk-modal__panel{width:100%;max-height:92vh;border-radius:16px 16px 0 0}.chat-bulk-form__datetime{grid-template-columns:repeat(2,1fr)}.chat-bulk-confirm dl{grid-template-columns:1fr}.chat-bulk-confirm dt{margin-top:8px}}
</style>
